import { useEffect, useRef, useState } from 'react';
import { Store } from 'lucide-react';

interface StoreData {
  id: string;
  name: string;
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  lat?: number;
  lng?: number;
  googleBusinessUrl?: string;
  logoUrl?: string | null;
}

interface StoreMapProps {
  stores: StoreData[];
  height?: string;
  className?: string;
  interactive?: boolean;
}

// Severna Park, MD coordinates as default center
const DEFAULT_CENTER = { lat: 39.0704, lng: -76.5615 };
const DEFAULT_ZOOM = 13;

// Load Google Maps script dynamically
function loadGoogleMapsScript(apiKey: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if (window.google?.maps) {
      resolve();
      return;
    }

    const existingScript = document.getElementById('google-maps-script');
    if (existingScript) {
      existingScript.addEventListener('load', () => resolve());
      return;
    }

    const script = document.createElement('script');
    script.id = 'google-maps-script';
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load Google Maps'));
    document.head.appendChild(script);
  });
}

// Geocode an address using Google Maps Geocoder
async function geocodeAddress(
  geocoder: google.maps.Geocoder,
  address: string
): Promise<google.maps.LatLngLiteral | null> {
  return new Promise((resolve) => {
    geocoder.geocode({ address }, (results, status) => {
      if (status === 'OK' && results && results[0]) {
        const location = results[0].geometry.location;
        resolve({ lat: location.lat(), lng: location.lng() });
      } else {
        console.warn('Geocoding failed for:', address, status);
        resolve(null);
      }
    });
  });
}

// Find place details using Places API
interface PlaceDetails {
  isOpen: boolean | null;
  openingHours: string[] | null;
  rating: number | null;
  placeId: string | null;
}

async function findPlaceDetails(
  storeName: string,
  address: string,
  location: google.maps.LatLngLiteral
): Promise<PlaceDetails> {
  try {
    const { Place } = await google.maps.importLibrary('places') as google.maps.PlacesLibrary;

    // Search for the place using the new API
    const query = `${storeName} ${address}`;
    console.log('Places API (new) searching for:', query);

    const request = {
      textQuery: query,
      fields: ['id', 'displayName', 'rating', 'regularOpeningHours', 'businessStatus'],
      locationBias: location,
      maxResultCount: 1,
    };

    // @ts-ignore - searchByText is new API
    const { places } = await Place.searchByText(request);

    if (places && places.length > 0) {
      const place = places[0];
      console.log('Found place:', place.displayName, 'id:', place.id);

      // Check if currently open using regularOpeningHours
      let isOpen: boolean | null = null;
      if (place.regularOpeningHours) {
        // Check if the place is open now
        const now = new Date();
        const dayOfWeek = now.getDay(); // 0 = Sunday
        const currentTime = now.getHours() * 100 + now.getMinutes();

        const periods = place.regularOpeningHours.periods;
        if (periods) {
          for (const period of periods) {
            if (period.open?.day === dayOfWeek) {
              const openTime = (period.open.hour || 0) * 100 + (period.open.minute || 0);
              const closeTime = period.close
                ? (period.close.hour || 0) * 100 + (period.close.minute || 0)
                : 2400;

              if (currentTime >= openTime && currentTime < closeTime) {
                isOpen = true;
                break;
              }
            }
          }
          if (isOpen === null && periods.length > 0) {
            isOpen = false; // Has hours but not currently in any open period
          }
        }
      }

      console.log('isOpen:', isOpen, 'rating:', place.rating);

      return {
        isOpen,
        openingHours: place.regularOpeningHours?.weekdayDescriptions ?? null,
        rating: place.rating ?? null,
        placeId: place.id ?? null,
      };
    }

    console.log('No places found');
    return { isOpen: null, openingHours: null, rating: null, placeId: null };
  } catch (e) {
    console.error('Places API error:', e);
    return { isOpen: null, openingHours: null, rating: null, placeId: null };
  }
}

export default function StoreMap({ stores, height = '300px', className = '', interactive = true }: StoreMapProps) {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<google.maps.Map | null>(null);
  const markersRef = useRef<google.maps.Marker[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;

    if (!apiKey) {
      setError('Google Maps API key not configured');
      setIsLoading(false);
      return;
    }

    let isMounted = true;

    async function initMap() {
      try {
        await loadGoogleMapsScript(apiKey);

        if (!isMounted || !mapRef.current) return;

        // Clear existing markers
        markersRef.current.forEach((marker) => marker.setMap(null));
        markersRef.current = [];

        // Create map
        const map = new google.maps.Map(mapRef.current, {
          center: DEFAULT_CENTER,
          zoom: DEFAULT_ZOOM,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: interactive,
          zoomControl: interactive,
          draggable: interactive,
          scrollwheel: interactive,
          disableDoubleClickZoom: !interactive,
          gestureHandling: interactive ? 'auto' : 'none',
        });
        mapInstanceRef.current = map;

        if (stores.length === 0) {
          setIsLoading(false);
          return;
        }

        const geocoder = new google.maps.Geocoder();
        const bounds = new google.maps.LatLngBounds();
        let hasValidMarkers = false;

        // Process stores
        for (const store of stores) {
          let position: google.maps.LatLngLiteral | null = null;

          // Use stored coordinates if available
          if (store.lat && store.lng) {
            position = { lat: store.lat, lng: store.lng };
          }
          // Otherwise geocode the address
          else if (store.address && store.city) {
            const fullAddress = [store.address, store.city, store.state, store.zipCode]
              .filter(Boolean)
              .join(', ');
            position = await geocodeAddress(geocoder, fullAddress);
          }

          if (position) {
            // Try to get place details for open/closed status
            let placeDetails: PlaceDetails = { isOpen: null, openingHours: null, rating: null, placeId: null };
            const storeAddress = [store.address, store.city, store.state].filter(Boolean).join(', ');
            try {
              placeDetails = await findPlaceDetails(store.name, storeAddress, position);
            } catch (e) {
              console.warn('Places API error for', store.name, e);
            }

            // Create marker with color based on open/closed status
            const markerColor = placeDetails.isOpen === true ? '#22c55e' :
                               placeDetails.isOpen === false ? '#ef4444' : '#3b82f6';

            const marker = new google.maps.Marker({
              position,
              map,
              title: store.name,
              animation: google.maps.Animation.DROP,
              icon: {
                path: google.maps.SymbolPath.CIRCLE,
                scale: 10,
                fillColor: markerColor,
                fillOpacity: 1,
                strokeColor: '#ffffff',
                strokeWeight: 2,
              },
            });

            // Build info window content with logo and status
            const logoHtml = store.logoUrl
              ? `<img src="${store.logoUrl}" alt="${store.name}" style="width: 50px; height: 50px; object-fit: contain; border-radius: 8px; margin-right: 10px; float: left;" />`
              : '';

            const statusHtml = placeDetails.isOpen === true
              ? '<span style="color: #22c55e; font-weight: bold;">Open Now</span>'
              : placeDetails.isOpen === false
              ? '<span style="color: #ef4444; font-weight: bold;">Closed</span>'
              : '';

            const ratingHtml = placeDetails.rating
              ? `<span style="color: #f59e0b; margin-left: 8px;">★ ${placeDetails.rating.toFixed(1)}</span>`
              : '';

            const directionsUrl = store.googleBusinessUrl ||
              `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
                [store.address, store.city, store.state, store.zipCode].filter(Boolean).join(', ')
              )}`;

            const infoContent = `
              <div style="min-width: 200px; padding: 8px; overflow: hidden;">
                ${logoHtml}
                <div style="overflow: hidden;">
                  <strong style="font-size: 14px;">${store.name}</strong>
                  ${statusHtml || ratingHtml ? `<br>${statusHtml}${ratingHtml}` : ''}
                  ${store.address ? `<br><span style="color: #666; font-size: 12px;">${store.address}</span>` : ''}
                  ${store.city ? `<br><span style="color: #666; font-size: 12px;">${store.city}${store.state ? `, ${store.state}` : ''}${store.zipCode ? ` ${store.zipCode}` : ''}</span>` : ''}
                  <br><a href="${directionsUrl}" target="_blank" rel="noopener" style="color: #3b82f6; font-size: 12px; text-decoration: none;">Get Directions →</a>
                </div>
              </div>
            `;

            const infoWindow = new google.maps.InfoWindow({
              content: infoContent,
            });

            marker.addListener('click', () => {
              infoWindow.open(map, marker);
            });

            markersRef.current.push(marker);
            bounds.extend(position);
            hasValidMarkers = true;
          }
        }

        // Fit map to show all markers
        if (hasValidMarkers) {
          if (stores.length === 1) {
            map.setCenter(bounds.getCenter());
            map.setZoom(15);
          } else {
            map.fitBounds(bounds, { top: 40, right: 40, bottom: 40, left: 40 });
          }
        }

        setIsLoading(false);
      } catch (err) {
        console.error('Error initializing map:', err);
        setError('Failed to load map');
        setIsLoading(false);
      }
    }

    initMap();

    return () => {
      isMounted = false;
      markersRef.current.forEach((marker) => marker.setMap(null));
      markersRef.current = [];
    };
  }, [stores, interactive]);

  if (error) {
    return (
      <div
        className={`flex items-center justify-center bg-gray-100 rounded-lg ${className}`}
        style={{ height }}
      >
        <div className="text-center text-gray-500">
          <Store className="w-8 h-8 mx-auto mb-2" />
          <p className="text-sm">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative">
      <div
        ref={mapRef}
        className={`rounded-lg overflow-hidden ${className}`}
        style={{ height, width: '100%' }}
      />
      {isLoading && (
        <div className="absolute inset-0 bg-white/70 flex items-center justify-center rounded-lg">
          <div className="text-sm text-gray-600">Loading map...</div>
        </div>
      )}
    </div>
  );
}

// TypeScript declarations for Google Maps
declare global {
  interface Window {
    google: typeof google;
  }
}
